import { createTheme, ThemeOptions } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";

/**
 * Base MUI theme shared between packages/ui and packages/landing.
 * Supports both light and dark modes with system preference detection.
 *
 * Usage:
 *   import { createThemeByMode } from '@rusha/shared-theme';
 *   const theme = createThemeByMode('light'); // or 'dark'
 *
 * See .agent/skills/ui-design-system/SKILL.md for design principles.
 */

// Shared color tokens (same in both modes)
const colors = {
  primary: {
    main: "#2563EB",
    light: "#60A5FA",
    dark: "#1D4ED8",
  },
  secondary: {
    main: "#10B981",
    light: "#34D399",
    dark: "#059669",
  },
  error: {
    main: "#EF4444",
    light: "#FCA5A5",
    dark: "#DC2626",
    contrastText: "#fff",
  },
  warning: {
    main: "#F59E0B",
    light: "#FCD34D",
    dark: "#D97706",
    contrastText: "#fff",
  },
  info: {
    main: "#3B82F6",
    light: "#93C5FD",
    dark: "#1D4ED8",
    contrastText: "#fff",
  },
  success: {
    main: "#10B981",
    light: "#6EE7B7",
    dark: "#059669",
    contrastText: "#fff",
  },
};

// Grey scale adapts to mode
const grey = {
  light: {
    50: "#F9FAFB",
    100: "#F3F4F6",
    200: "#E5E7EB",
    300: "#D1D5DB",
    400: "#9CA3AF",
    500: "#6B7280",
    600: "#4B5563",
    700: "#374151",
    800: "#1F2937",
    900: "#111827",
  },
  dark: {
    50: "#F1F5F9",
    100: "#E2E8F0",
    200: "#CBD5E1",
    300: "#94A3B8",
    400: "#64748B",
    500: "#475569",
    600: "#334155",
    700: "#1E293B",
    800: "#0F172A",
    900: "#020617",
  },
};

// Light mode palette
const lightPalette = {
  mode: "light" as const,
  primary: colors.primary,
  secondary: colors.secondary,
  error: colors.error,
  warning: colors.warning,
  info: colors.info,
  success: colors.success,
  background: {
    default: "#F8FAFC",
    paper: "#FFFFFF",
  },
  text: {
    primary: "#111827",
    secondary: "#4B5563",
  },
  grey: grey.light,
  divider: "rgba(17, 24, 39, 0.08)",
};

// Dark mode palette
const darkPalette = {
  mode: "dark" as const,
  primary: colors.primary,
  secondary: colors.secondary,
  error: colors.error,
  warning: colors.warning,
  info: colors.info,
  success: colors.success,
  background: {
    default: "#0F172A",
    paper: "#1E293B",
  },
  text: {
    primary: "#F1F5F9",
    secondary: "#94A3B8",
  },
  grey: grey.dark,
  divider: "rgba(255, 255, 255, 0.08)",
};

// Common typography, shape, components (same for both modes)
const commonThemeOptions: ThemeOptions = {
  spacing: 8,
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontWeight: 800,
      letterSpacing: "-0.025em",
      fontSize: "2.5rem",
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 700,
      letterSpacing: "-0.025em",
      fontSize: "2rem",
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      letterSpacing: "-0.02em",
      fontSize: "1.5rem",
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.25rem",
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.125rem",
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: "1rem",
      lineHeight: 1.4,
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: "1rem",
      lineHeight: 1.5,
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: "0.875rem",
      lineHeight: 1.5,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.7,
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.7,
    },
    button: {
      textTransform: "none" as const,
      fontWeight: 600,
      fontSize: "0.875rem",
    },
    caption: {
      fontSize: "0.75rem",
      lineHeight: 1.5,
    },
    overline: {
      fontFamily: '"Roboto Mono", monospace',
      fontSize: "0.75rem",
      letterSpacing: "0.5px",
      textTransform: "uppercase" as const,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: "0.625rem 1.25rem",
          fontWeight: 600,
          textTransform: "none",
          transition: "all 150ms ease",
          "&:hover": {
            transform: "translateY(-1px)",
          },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, #2563EB 0%, #60A5FA 100%)`,
          boxShadow: `0 2px 8px ${alpha("#2563EB", 0.2)}`,
          "&:hover": {
            background: `linear-gradient(135deg, #1D4ED8 0%, #2563EB 100%)`,
            boxShadow: `0 4px 12px ${alpha("#2563EB", 0.3)}`,
          },
        },
        containedSecondary: {
          background: `linear-gradient(135deg, #059669 0%, #10B981 100%)`,
          boxShadow: `0 2px 8px ${alpha("#10B981", 0.2)}`,
          "&:hover": {
            background: `linear-gradient(135deg, #047857 0%, #059669 100%)`,
            boxShadow: `0 4px 12px ${alpha("#10B981", 0.3)}`,
          },
        },
        outlined: {
          borderWidth: "1.5px",
          "&:hover": {
            borderWidth: "1.5px",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
        filledPrimary: {
          background: `linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)`,
          boxShadow: `0 2px 5px ${alpha("#2563EB", 0.2)}`,
        },
        filledSecondary: {
          background: `linear-gradient(135deg, #059669 0%, #10B981 100%)`,
          boxShadow: `0 2px 5px ${alpha("#10B981", 0.2)}`,
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          "@media (min-width: 600px)": {
            minHeight: "64px",
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: "none",
          fontWeight: 500,
          "&:hover": {
            textDecoration: "none",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: {
          border: `1px solid rgba(17, 24, 39, 0.08)`,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          fontSize: "0.75rem",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "#4B5563",
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 6,
          borderRadius: 3,
        },
        bar: {
          borderRadius: 3,
        },
      },
    },
  },
};

/**
 * Create a theme for the specified mode
 */
export const createThemeByMode = (mode: "light" | "dark"): ReturnType<typeof createTheme> => {
  const palette = mode === "light" ? lightPalette : darkPalette;
  return createTheme({ ...commonThemeOptions, palette });
};

/**
 * Legacy export - creates a light theme (backward compatible)
 * @deprecated Use createThemeByMode instead
 */
export const baseTheme = createThemeByMode("light");

// Export both for convenience
export const lightTheme = createThemeByMode("light");
export const darkTheme = createThemeByMode("dark");

// Re-export PaletteMode for type safety
export type { PaletteMode } from "@mui/material";
